import {Component, EventEmitter, Input, Output, OnChanges, SimpleChanges} from '@angular/core';
import {CsIconModule} from '@components/icon';
import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import {Regimen} from '@cureskin/api-client/src/server-api/api';
import {Catalog} from '@cureskin/api-client/src/server-api';
import {TimeService} from '@services/time-service';
import {AppConfig} from '../../../app.config';
import {CollapsableCardItem, CollapsableCardState} from '../../../app-constant-types';

export enum KnowMoreClicked {
  'AUTO',
  'NEW',
  'NOT_YET_STARTED',
}

@Component({
  selector: 'app-instruction-card',
  templateUrl: './instruction-card.component.html',
  imports: [
    CsIconModule,
    NgClass,
    NgStyle,
    NgTemplateOutlet
],
  standalone: true,
})
export class InstructionCardComponent implements OnChanges {
  @Input() item: any;
  @Input() isDayTime: boolean;
  @Input() todayDayVal?: number;
  @Input() days?: any[];
  @Input() user: any;
  @Input() userLanguage: string;
  @Input() index?: number;
  @Input() dayAfterDelivery?: number;
  @Input() regimen?: Regimen;
  @Input() changeInstructionForRepairProduct?: boolean;
  @Input() removedProducts?: Catalog[];
  @Input() recentlyDeliveredMargIds?: string[];
  @Input() collapsableCardState: CollapsableCardState = {
    onHold: [],
    removed: [],
    instructionChanged: [],
  };
  @Input() fromOthersSection?: boolean;

  @Output() playButtonClick: EventEmitter<any> = new EventEmitter<any>();
  @Output() knowMoreClicked: EventEmitter<any> = new EventEmitter<any>();
  @Output() toggleProductCollapse: EventEmitter<any> = new EventEmitter<any>();

  private readonly PRODUCT_DELIVERY_WINDOW_DAYS: number = 7;

  // Simple computed properties - set once when inputs change
  isCurrentProductDelivered: boolean = false;
  isCurrentProductAdded: boolean = false;
  isCurrentInstructionChanged: boolean = false;
  isCurrentProductRecentlyBought: boolean = false;
  isCurrentProductEverDelivered: boolean = false;
  currentKnowMoreClickType: KnowMoreClicked = KnowMoreClicked.NOT_YET_STARTED;
  isCurrentRemovedProductsInstruction: boolean = false;
  hasActiveOrderPlaced: boolean = false;

  constructor(
    public readonly appConfig: AppConfig,
    private readonly timeService: TimeService,
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    // Recompute all values when inputs change
    if (changes['item'] || changes['regimen'] || changes['collapsableCardState'] || changes['recentlyDeliveredMargIds']) {
      this.computeCurrentItemProperties();
    }
  }

  private computeCurrentItemProperties(): void {
    // Get the correct product ID - handle both objectId and id
    const productObjectId = this.item?.product?.objectId;
    const productId = this.item?.product?.id;

    // 1. Is product delivered?
    this.isCurrentProductDelivered = this.computeIsProductDelivered(productObjectId);

    // 2. Is product added?
    this.isCurrentProductAdded = !!this.item?.addedTimestamp;

    // 3. Is instruction changed?
    this.isCurrentInstructionChanged = productObjectId ?
      this.collapsableCardState.instructionChanged.includes(productObjectId) : false;

    // 4. Is product recently bought?
    this.isCurrentProductRecentlyBought = this.item?.product?.margId ?
      (Array.isArray(this.recentlyDeliveredMargIds) && this.recentlyDeliveredMargIds.includes(this.item.product.margId)) : false;

    // 5. Has product ever been delivered?
    this.isCurrentProductEverDelivered = this.hasProductEverBeenDelivered(productObjectId);

    // 6. Know more click type
    this.currentKnowMoreClickType = this.isCurrentProductDelivered
      ? KnowMoreClicked.NEW
      : KnowMoreClicked.NOT_YET_STARTED;

    // 7. Is removed product instruction? - Check both objectId and id
    this.isCurrentRemovedProductsInstruction = this.checkIfRemovedProduct(productObjectId, productId);

    // 8. Is product order placed? - Check both objectId and id
    this.hasActiveOrderPlaced = this.computeIsProductOrderPlaced(productObjectId, productId);
  }

  private checkIfRemovedProduct(objectId: string, id: string): boolean {
    if (!this.collapsableCardState?.removed?.length) return false;

    // Check both objectId and id to handle different data structures
    return this.collapsableCardState.removed.some((removedItem: CollapsableCardItem) =>
      removedItem.productId === objectId || removedItem.productId === id
    );
  }

  private computeIsProductDelivered(productId: string): boolean {
    if (!productId || !this.regimen?.productTimestamps) return false;

    const deliveredTimestamp = this.regimen.productTimestamps?.[productId]?.deliveredTimestamp?.iso;
    if (!deliveredTimestamp) return false;

    const deliveredDate = new Date(deliveredTimestamp);
    const currentDate = new Date();
    const diffDays = this.timeService.differenceInDays(currentDate, deliveredDate);
    return diffDays <= this.PRODUCT_DELIVERY_WINDOW_DAYS;
  }

  private hasProductEverBeenDelivered(productId: string): boolean {
    if (!productId || !this.regimen?.productTimestamps) return false;
    return !!this.regimen.productTimestamps?.[productId]?.deliveredTimestamp?.iso;
  }

  private computeIsProductOrderPlaced(objectId: string, id: string): boolean {
    if (!this.regimen?.productTimestamps) return false;

    const productIds = [objectId, id].filter(Boolean);
    let hasOrderPlaced = false;

    for (const productId of productIds) {
      const timestamps = this.regimen.productTimestamps?.[productId];
      if (!timestamps) continue;

      const deliveredTimestamp = timestamps.deliveredTimestamp?.iso;
      const orderPlacedTimestamp = timestamps.orderPlacedTimestamp?.iso;

      if (orderPlacedTimestamp) {
        hasOrderPlaced = true;
        // If delivered exists and is more recent than order placed, don't consider it as order placed
        if (deliveredTimestamp && new Date(deliveredTimestamp) > new Date(orderPlacedTimestamp)) {
          return false;
        }
      }
    }

    return hasOrderPlaced;
  }

  // Event handlers
  onPlayButtonClick(): void {
    this.playButtonClick.emit(this.item);
  }

  onKnowMoreClicked(fromInstruction: KnowMoreClicked): void {
    this.knowMoreClicked.emit({ item: this.item, fromInstruction });
  }

  onToggleProductCollapse(productId: string): void {
    this.toggleProductCollapse.emit(productId);
  }

  // Simple functions for template usage with parameters - no caching needed
  isOnHoldCardOpen(productId: string): boolean {
    return !!this.collapsableCardState.onHold.find((p: CollapsableCardItem): boolean => p.productId === productId)?.isOpen;
  }

  isRemovedProductsInstructionOpen(productId: string): boolean {
    return !!this.collapsableCardState.removed.find((p: CollapsableCardItem): boolean => p.productId === productId)?.isOpen;
  }

  isValidValue(value: string | number | null | undefined): boolean {
    if (value == null || typeof value === 'number') return value != null;
    const normalized = String(value).trim().toLowerCase();
    return normalized !== '' && normalized !== 'n/a' && normalized !== 'na';
  }
}
